/**
 * DecalGhostModels — renders transparent ghost shapes for decal features
 * (displayCutout, cameraLens, homeIndicator) during slider/pad drag.
 *
 * Uses simple THREE.js geometry (ShapeGeometry for rounded rects, CircleGeometry
 * for lenses) instead of expensive DecalGeometry to keep drag performance smooth.
 */
import {useMemo, useEffect, useRef, useSyncExternalStore} from 'react'
import * as THREE from 'three'
import {createRoundedRectShape} from '../../../utils/geometry'
import {useAppSelector} from '../../../store'
import {selectActiveScene} from '../../store/scenesSlice'
import {getPreviewDecalFeatureSnapshot, subscribePreviewDecalFeature} from '../previewDecalFeatures'
import type {PreviewDisplayCutout, PreviewCameraLens, PreviewHomeIndicator} from '../previewDecalFeatures'
import {GHOST_COLOR, GHOST_OPACITY} from './ghostStyle'
const Z_OFFSET = 0.15

// ── Single cutout ghost ──────────────────────────────────────────

const CutoutGhost = ({
  xOffset,
  yOffset,
  width,
  height,
  cornerRadius,
  ghostZ,
}: PreviewDisplayCutout & {ghostZ: number}) => {
  const prevRef = useRef<THREE.ShapeGeometry | null>(null)
  const geo = useMemo(() => {
    const shape = createRoundedRectShape(width, height, cornerRadius)
    return new THREE.ShapeGeometry(shape, 8)
  }, [width, height, cornerRadius])

  useEffect(() => {
    if (prevRef.current && prevRef.current !== geo) prevRef.current.dispose()
    prevRef.current = geo
    return () => {
      geo.dispose()
    }
  }, [geo])

  return (
    <mesh geometry={geo} position={[xOffset, yOffset, ghostZ]}>
      <meshBasicMaterial
        color={GHOST_COLOR}
        transparent
        opacity={GHOST_OPACITY}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

// ── Single camera lens ghost ─────────────────────────────────────

const LensGhost = ({xOffset, yOffset, radius, ghostZ}: PreviewCameraLens & {ghostZ: number}) => {
  const prevRef = useRef<THREE.CircleGeometry | null>(null)
  const geo = useMemo(() => new THREE.CircleGeometry(radius, 32), [radius])

  useEffect(() => {
    if (prevRef.current && prevRef.current !== geo) prevRef.current.dispose()
    prevRef.current = geo
    return () => {
      geo.dispose()
    }
  }, [geo])

  return (
    <mesh geometry={geo} position={[xOffset, yOffset, ghostZ]}>
      <meshBasicMaterial
        color={GHOST_COLOR}
        transparent
        opacity={GHOST_OPACITY}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

// ── Single home indicator ghost ──────────────────────────────────

const IndicatorGhost = ({yOffset, width, height, cornerRadius, ghostZ}: PreviewHomeIndicator & {ghostZ: number}) => {
  const prevRef = useRef<THREE.ShapeGeometry | null>(null)
  const geo = useMemo(() => {
    const shape = createRoundedRectShape(width, height, cornerRadius)
    return new THREE.ShapeGeometry(shape, 8)
  }, [width, height, cornerRadius])

  useEffect(() => {
    if (prevRef.current && prevRef.current !== geo) prevRef.current.dispose()
    prevRef.current = geo
    return () => {
      geo.dispose()
    }
  }, [geo])

  return (
    <mesh geometry={geo} position={[0, yOffset, ghostZ]}>
      <meshBasicMaterial
        color={GHOST_COLOR}
        transparent
        opacity={GHOST_OPACITY}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

// ── Container ────────────────────────────────────────────────────

const DecalGhostModels = () => {
  const previewMap = useSyncExternalStore(subscribePreviewDecalFeature, getPreviewDecalFeatureSnapshot)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)

  const frontZ = deviceDepth / 2
  const ghostZ = frontZ + Z_OFFSET

  if (!previewMap) return null

  return (
    <group>
      {Object.entries(previewMap).map(([id, p]) => {
        switch (p.type) {
          case 'displayCutout':
            return <CutoutGhost key={id} {...p} ghostZ={ghostZ} />
          case 'cameraLens':
            return <LensGhost key={id} {...p} ghostZ={ghostZ} />
          case 'homeIndicator':
            return <IndicatorGhost key={id} {...p} ghostZ={ghostZ} />
        }
      })}
    </group>
  )
}

export default DecalGhostModels
